/**
 * Carousel — embla-carousel-react-backed slide deck for narrative demos.
 *
 * Long-form CSK demos frequently want to walk the user through a
 * sequence of slides — "here is the seam", "here is the witness",
 * "here is the receipt" — without committing to a full manuscript
 * viewer. The carousel keeps the slide DOM mounted (so each slide can
 * persist its own state), exposes prev / next controls, and emits the
 * current slide index so the host can sync narration, analytics, or
 * a step indicator.
 *
 * Single responsibility: the slide viewport + navigation chrome.
 * Auto-advance, looping, and snap behavior come from Embla's options.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { GRAY, PRIMARY } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export interface CarouselSlide {
  /** Stable identifier — also drives the accessible slide label. */
  readonly id: string;
  /** Slide content. */
  readonly content: React.ReactNode;
  /** Optional title rendered above the slide content. */
  readonly title?: React.ReactNode;
}

export interface CarouselProps {
  /** Slides rendered into the viewport. */
  readonly slides: readonly CarouselSlide[];
  /** Whether the slide deck loops. Defaults to false. */
  readonly loop?: boolean;
  /** Whether to render the prev / next controls. Defaults to true. */
  readonly showControls?: boolean;
  /** Whether to render the dot indicator strip. Defaults to true. */
  readonly showDots?: boolean;
  /** Accessible label for the surrounding region. */
  readonly ariaLabel: string;
  /** Optional handler fired when the active slide index changes. */
  readonly onSlideChange?: (index: number) => void;
  /** Additional className applied to the wrapper. */
  readonly className?: string;
}

type EmblaApi = NonNullable<UseEmblaCarouselType[1]>;

const VIEWPORT_STYLE: React.CSSProperties = {
  overflow: "hidden",
};

const TRACK_STYLE: React.CSSProperties = {
  display: "flex",
  gap: "0.75rem",
};

const SLIDE_STYLE: React.CSSProperties = {
  flex: "0 0 100%",
  minWidth: 0,
};

/** Renders a paged slide deck with prev / next / dot indicators. */
export const Carousel = React.memo(function Carousel({
  slides,
  loop = false,
  showControls = true,
  showDots = true,
  ariaLabel,
  onSlideChange,
  className,
}: CarouselProps): React.JSX.Element {
  const [viewportRef, emblaApi] = useEmblaCarousel({ loop });
  const [selected, setSelected] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(slides.length > 1);

  const syncState = useCallback(
    (api: EmblaApi) => {
      const index = api.selectedScrollSnap();
      setSelected(index);
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
      onSlideChange?.(index);
    },
    [onSlideChange],
  );

  useEffect(() => {
    if (emblaApi === undefined) return;
    syncState(emblaApi);
    emblaApi.on("select", syncState);
    emblaApi.on("reInit", syncState);
    return (): void => {
      emblaApi.off("select", syncState);
      emblaApi.off("reInit", syncState);
    };
  }, [emblaApi, syncState]);

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);
  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);
  const scrollTo = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  return (
    <section
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      data-csk-carousel
      className={cn("font-body", className)}
    >
      <div ref={viewportRef} style={VIEWPORT_STYLE} data-csk-carousel-viewport>
        <div style={TRACK_STYLE}>
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              role="group"
              aria-roledescription="slide"
              aria-label={`Slide ${index + 1} of ${slides.length}`}
              data-csk-carousel-slide={slide.id}
              data-csk-carousel-active={index === selected ? "true" : "false"}
              style={SLIDE_STYLE}
            >
              {slide.title !== undefined && (
                <div
                  style={{
                    color: GRAY[700],
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    marginBottom: "0.4rem",
                    textTransform: "uppercase",
                  }}
                >
                  {slide.title}
                </div>
              )}
              {slide.content}
            </div>
          ))}
        </div>
      </div>
      {(showControls || showDots) && (
        <div
          style={{
            alignItems: "center",
            display: "flex",
            gap: "0.75rem",
            justifyContent: "space-between",
            marginTop: "0.75rem",
          }}
        >
          {showControls && (
            <button
              type="button"
              aria-label="Previous slide"
              onClick={scrollPrev}
              disabled={!canPrev}
              data-csk-carousel-prev
              style={{
                background: GRAY[100],
                border: `1px solid ${GRAY[300]}`,
                borderRadius: "0.375rem",
                color: GRAY[700],
                cursor: canPrev ? "pointer" : "not-allowed",
                opacity: canPrev ? 1 : 0.45,
                padding: "0.3rem 0.65rem",
              }}
            >
              ←
            </button>
          )}
          {showDots && (
            <div style={{ display: "flex", gap: "0.4rem" }} aria-hidden="true">
              {slides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => {
                    scrollTo(index);
                  }}
                  data-csk-carousel-dot={index}
                  data-csk-carousel-dot-active={index === selected ? "true" : "false"}
                  style={{
                    background: index === selected ? PRIMARY[600] : GRAY[300],
                    border: 0,
                    borderRadius: "9999px",
                    cursor: "pointer",
                    height: 8,
                    padding: 0,
                    width: 8,
                  }}
                />
              ))}
            </div>
          )}
          {showControls && (
            <button
              type="button"
              aria-label="Next slide"
              onClick={scrollNext}
              disabled={!canNext}
              data-csk-carousel-next
              style={{
                background: GRAY[100],
                border: `1px solid ${GRAY[300]}`,
                borderRadius: "0.375rem",
                color: GRAY[700],
                cursor: canNext ? "pointer" : "not-allowed",
                opacity: canNext ? 1 : 0.45,
                padding: "0.3rem 0.65rem",
              }}
            >
              →
            </button>
          )}
        </div>
      )}
    </section>
  );
});
